import React, {useEffect, useState} from 'react';
import {useLocation, useRoutes, matchRoutes, BrowserRouter as Router, Routes, Route} from "react-router-dom";
import {UserProvider} from "../_contexts/UserContext";
import Login from "./Login";
import Header from "./_ui/Header";
import Footer from "./_ui/Footer";
import Ticket from "./Ticket";
import ClientDashboard from "./ClientDashboard";
import AgentDashboard from "./AgentDashboard";
import AdminDashboard from "./AdminDashboard";
import Invoice from "./Invoice";
import ForgotPassword from "./ForgotPassword";
import ResetPassword from "./ResetPassword";
import VerifyEmail from "./VerifyEmail";
import PrivateRoute from "./Helpers/PrivateRoute";
import '../styles.scss';
import Agreement from "./Agreement";
import AdminInvoices from "./AdminInvoices";
import ClientEditor from "./ClientEditor";
import RegistrationForm from "./_ui/formElements/RegistrationForm";
import AdminStats from "./AdminStats";
import AdminSettings from "./AdminSettings";
import {DashboardProvider} from "../_contexts/DashboardContext";
import {initGA, pageview} from "../system/ga";
import WebsiteReviewManager from "./admin/WebsiteReviewManager";
import ReviewEditor from "./admin/ReviewEditor";

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || null;

const appRoutes = [
    { path: "/", element: <Login/>, handle: { title: 'Login' } },
    { path: "login", element: <Login/>, handle: { title: 'Login' } },
    { path: "register", element: <RegistrationForm/>, handle: { title: 'Registration' } },
    { path: "verifyemail/:verificationCode", element: <VerifyEmail/>, handle: { title: 'Verify Email' } },
    { path: "forgotpassword", element: <ForgotPassword/>, handle: { title: 'Forgot Password' } },
    { path: "resetpassword/:resetCode", element: <ResetPassword/>, handle: { title: 'Reset Password' } },
    { path: "admins", element: <PrivateRoute><DashboardProvider><AdminDashboard/></DashboardProvider></PrivateRoute>, handle: { title: 'Admin Dashboard' } },
    { path: "admins/invoices", element: <PrivateRoute><AdminInvoices/></PrivateRoute>, handle: { title: 'Admin Invoices' } },
    { path: "admins/invoices/:offset", element: <PrivateRoute><AdminInvoices/></PrivateRoute>, handle: { title: 'Admin Invoices' } },
    { path: "admins/stats", element: <PrivateRoute><AdminStats/></PrivateRoute>, handle: { title: 'Admin Stats' } },
    { path: "admins/settings", element: <PrivateRoute><AdminSettings/></PrivateRoute>, handle: { title: 'Admin Settings' } },
    { path: "admins/settings/:tab", element: <PrivateRoute><AdminSettings/></PrivateRoute>, handle: { title: 'Admin Settings' } },
    { path: "admins/settings/:tab/:section", element: <PrivateRoute><AdminSettings/></PrivateRoute>, handle: { title: 'Admin Settings' } },
    { path: "admins/reviews", element: <PrivateRoute><WebsiteReviewManager/></PrivateRoute>, handle: { title: 'Website Reviews' } },
    { path: "admins/reviews/:id", element: <PrivateRoute><ReviewEditor/></PrivateRoute>, handle: { title: 'Review Editor' } },
    { path: "agents", element: <PrivateRoute><DashboardProvider><AgentDashboard/></DashboardProvider></PrivateRoute>, handle: { title: 'Agent Dashboard' } },
    { path: "agents/:agentId/:tab", element: <PrivateRoute><DashboardProvider><AgentDashboard/></DashboardProvider></PrivateRoute>, handle: { title: 'Agent Dashboard' } },
    { path: "clients", element: <PrivateRoute><DashboardProvider><ClientDashboard/></DashboardProvider></PrivateRoute>, handle: { title: 'Client Dashboard' } },
    { path: "clients/:clientId", element: <PrivateRoute><DashboardProvider><ClientDashboard/></DashboardProvider></PrivateRoute>, handle: { title: 'Client Dashboard' } },
    { path: "clients/:clientId/:tab", element: <PrivateRoute><ClientEditor/></PrivateRoute>, handle: { title: 'Client Editor' } },
    { path: "clients/:clientId/invoices/:invoiceId", element: <PrivateRoute><Invoice/></PrivateRoute>, handle: { title: 'Invoice' } },
    { path: "clients/:clientId/tickets/:ticketId", element: <PrivateRoute><Ticket/></PrivateRoute>, handle: { title: 'Ticket' } },
    { path: "agreement", element: <Agreement/>, handle: { title: 'Agreement' } },
];

function App() {
    return (
        <React.StrictMode>
            <UserProvider>
                <div id='app-wrapper'>
                    <Router>
                        <RouteTracker measurementId={GA_MEASUREMENT_ID} />
                        <Header/>
                        <div id='component-wrapper' className={`bg-light border-bottom-1`}>
                            <RoutesRenderer/>
                        </div>
                    </Router>
                    <Footer/>
                </div>
            </UserProvider>
        </React.StrictMode>
    );
}

/**
 * Interpolate title template with params
 * @param template
 * @param params
 * @returns {*}
 */
function interpolateTitle(template, params = {}) {
    if (!template) return template;
    if( !params || Object.keys(params).length === 0) return template;
    if( params.tab ){
        template += ` - ${params.tab.charAt(0).toUpperCase() + params.tab.slice(1)}`;
    }
    if( params.section ){
        template += ` - ${params.section.charAt(0).toUpperCase() + params.section.slice(1)}`;
    }
    return template.replace(/:([a-zA-Z0-9_]+)/g, (_, key) => {
        const val = params[key];
        if (val == null) return '';
        return String(val)
        .replace(/[-_]/g, ' ')
        .split(' ')
        .map(s => s.charAt(0).toUpperCase() + s.slice(1))
        .join(' ');
    });
}

/**
 * Track route changes for GA
 * @param measurementId
 * @returns {null}
 * @constructor
 */
function RouteTracker({ measurementId }) {
    const location = useLocation();

    React.useEffect(() => {
        initGA(measurementId);
    }, [measurementId]);

    React.useEffect(() => {
        const matches = matchRoutes(appRoutes,location) || [];
        const lastMatch = matches.length ? matches[matches.length - 1] : null;
        const rawTitle = lastMatch?.route?.handle?.title;
        const title = rawTitle ? interpolateTitle(rawTitle, lastMatch.params) : (document.title || 'User X Support');
        document.title = `${title} | User X Support`;
        pageview(location.pathname + location.search, title);
    }, [location]);

    return null;
}

function RoutesRenderer() {
    return useRoutes(appRoutes);
}

export default App;
